import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem('enatAssistantSession');
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('enatAssistantSession', id);
  }
  return id;
};

export const AssistantWidget = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const lang = language === 'en' ? 'en' : 'fr';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (location.pathname.startsWith('/admin')) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setIsStreaming(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (res.status === 429) {
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: 'assistant', content: lang === 'fr' ? 'Vous avez atteint la limite de messages pour le moment. Merci de réessayer un peu plus tard, ou écrivez-nous à info@fondationenat.org.' : 'You have reached the message limit for now. Please try again a bit later, or write to info@fondationenat.org.' };
          return next;
        });
        setIsStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          const data = JSON.parse(payload);
          if (data.error) {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: 'assistant', content: lang === 'fr' ? 'Désolée, une erreur est survenue. Réessayez ou écrivez-nous à info@fondationenat.org.' : 'Sorry, an error occurred. Try again or write to info@fondationenat.org.' };
              return next;
            });
          } else if (data.delta) {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + data.delta };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: 'assistant', content: lang === 'fr' ? 'Connexion impossible. Réessayez plus tard.' : 'Connection failed. Please try again later.' };
        return next;
      });
    }
    setIsStreaming(false);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={lang === 'fr' ? "Ouvrir l'assistant" : 'Open assistant'}
          data-testid="assistant-open-btn"
          className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-[#4A708B] text-white shadow-xl flex items-center justify-center hover:bg-[#3B5A6F] hover:scale-105 transition-all"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-6 left-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] max-h-[75vh] bg-white rounded-3xl shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden"
        >
          <div className="bg-[#4A708B] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <div>
                <p className="font-semibold text-sm">{lang === 'fr' ? 'Assistante Enat' : 'Enat Assistant'}</p>
                <p className="text-xs text-white/70">{lang === 'fr' ? 'Réponses instantanées' : 'Instant answers'}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={lang === 'fr' ? 'Fermer' : 'Close'}
              data-testid="assistant-close-btn"
              className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-sm text-[#546A76] bg-[#FAFAFA] rounded-2xl p-4" data-testid="assistant-welcome">
                {lang === 'fr'
                  ? 'Bonjour ! Je peux vous renseigner sur nos services, les dons, la boutique ou les événements. Comment puis-je vous aider ?'
                  : 'Hello! I can help with our services, donations, the shop or events. How can I help you?'}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === 'user' ? 'bg-[#4A708B] text-white' : 'bg-[#FAFAFA] text-[#1A252C] border border-[#E2E8F0]'
                }`}>
                  {m.content || (isStreaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-[#4A708B]" /> : '')}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 pb-1.5">
            <p className="text-[10px] text-[#546A76] leading-snug">
              {lang === 'fr' ? 'Urgence : 911 · SOS violence conjugale 1-800-363-9010' : 'Emergency: 911 · SOS violence conjugale 1-800-363-9010'}
            </p>
          </div>
          <div className="p-3 border-t border-[#E2E8F0] flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={lang === 'fr' ? 'Écrivez votre message...' : 'Type your message...'}
              data-testid="assistant-input"
              className="flex-1 rounded-full border border-[#E2E8F0] px-4 py-2.5 text-sm focus:outline-none focus:border-[#4A708B]"
            />
            <button
              type="button"
              onClick={send}
              disabled={isStreaming || !input.trim()}
              aria-label={lang === 'fr' ? 'Envoyer' : 'Send'}
              data-testid="assistant-send-btn"
              className="w-10 h-10 rounded-full bg-[#D4AF37] text-[#1A252C] flex items-center justify-center hover:bg-[#e6c455] disabled:opacity-40 transition-colors flex-shrink-0"
            >
              {isStreaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AssistantWidget;
